import { useLayoutEffect, useState } from 'react';

/**
 * {top,left,flipUp} for a popover/toolbar anchored to `anchorRef`, meant to
 * be rendered through a portal to document.body. Editable blocks live
 * inside `.fsd-reveal` sections, each of which gets its own CSS stacking
 * context once its scroll-in animation runs (transform + opacity with
 * fill-mode:forwards) — a popover positioned merely `absolute` inside one of
 * those sections can end up visually painted UNDER a later sibling
 * section's stacking context, making parts of it (e.g. a file input)
 * unclickable even though it's visibly on screen. Portaling to <body>
 * sidesteps that entirely.
 *
 * Coordinates are plain viewport-relative (straight from getBoundingClientRect,
 * no scrollY/scrollX added) since `position: fixed` here IS resolved against
 * the viewport — kept in sync during scrolling by recomputing on every
 * `scroll`/`resize` event rather than trying to precompute a document-relative
 * offset. For the "above" placement we don't know the popover's height in
 * advance, so `flipUp` is returned and the caller must apply
 * `transform: translateY(-100%)` to anchor by the box's bottom edge instead
 * of computing a bottom offset.
 *
 * `contentRef` (optional): pass the SAME ref the caller already attaches to
 * its popover/toolbar root (the one used for its click-outside check). Once
 * that element exists, its real rendered width is used to clamp `left` so
 * the box can't run off the right edge of the viewport — e.g. a field near
 * the right-hand column of a two-column layout, where anchoring by the
 * anchor's own left edge alone would push a ~300-560px-wide toolbar past the
 * viewport boundary.
 *
 * Deliberately simple: only viewport-edge distance decides above/below —
 * an earlier version also tried to detect and dodge nearby sibling content
 * (e.g. the row above in a packed form grid), but that pushed the toolbar
 * arbitrarily far from the text being edited in some layouts, which reads as
 * far more broken than the small overlap it was trying to avoid. Anchored
 * close to the text, consistently, is the priority.
 */
const MIN_SPACE = 220; // rough floor for "enough room to not look cramped"
const H_MARGIN = 8;

export function useFixedPopoverPosition(anchorRef, open, preferred = 'below', contentRef) {
  const [pos, setPos] = useState(null);

  useLayoutEffect(() => {
    if (!open || !anchorRef.current) {
      setPos(null);
      return undefined;
    }
    const update = () => {
      if (!anchorRef.current) return;
      const r = anchorRef.current.getBoundingClientRect();
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      // Flip off the preferred side only when it's cramped AND the other side
      // genuinely has more room — otherwise keep the preferred side (avoids
      // flapping between above/below when both are merely "not great").
      const goAbove =
        preferred === 'above'
          ? !(spaceAbove < MIN_SPACE && spaceBelow > spaceAbove)
          : spaceBelow < MIN_SPACE && spaceAbove > spaceBelow;

      let left = r.left;
      const contentEl = contentRef?.current;
      if (contentEl) {
        const w = contentEl.offsetWidth;
        if (left + w > window.innerWidth - H_MARGIN) {
          left = Math.max(H_MARGIN, window.innerWidth - w - H_MARGIN);
        }
      }

      if (goAbove) {
        setPos({ left, top: r.top - 8, flipUp: true });
      } else {
        setPos({ left, top: r.bottom + 8, flipUp: false });
      }
    };
    update();
    // A nearby section's scroll-reveal (transform+opacity, 0.55s) may still be
    // settling at the exact moment of the click — recheck shortly after so the
    // popover doesn't stay pinned to a mid-animation anchor position.
    const raf = requestAnimationFrame(update);
    const settleTimer = window.setTimeout(update, 600);
    window.addEventListener('scroll', update, true);
    window.addEventListener('resize', update);
    return () => {
      cancelAnimationFrame(raf);
      window.clearTimeout(settleTimer);
      window.removeEventListener('scroll', update, true);
      window.removeEventListener('resize', update);
    };
  }, [open, anchorRef, preferred, contentRef]);

  return pos;
}
